import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Ban, Eye } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API, formatApiErrorDetail } from "../../lib/operatorWalk";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  expired: "bg-slate-100 text-slate-500",
  revoked: "bg-red-50 text-red-600",
};

function fmtDate(iso) {
  if (!iso) return ".";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function SharedLinksPanel({ refreshKey }) {
  const { authHeader } = usePortalAuth();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${OW_API}/portal/shares`, { headers: authHeader });
      setShares(res.data.shares || []);
    } catch {
      setShares([]);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this link? Anyone who has it will immediately lose access.")) return;
    setBusy(id);
    try {
      await axios.post(`${OW_API}/portal/shares/${id}/revoke`, {}, { headers: authHeader });
      toast.success("Link revoked.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="bg-white border border-brand-200" data-testid="portal-shared-links-panel">
      <div className="px-6 py-4 border-b border-brand-100">
        <h2 className="font-serif text-2xl text-brand-700">Shared Links</h2>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="flex items-center text-ink-muted text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading
          </div>
        ) : shares.length === 0 ? (
          <p className="text-ink-muted text-sm" data-testid="shared-links-empty">
            You have not shared any reports yet. Use the Share button on any report to create a private link.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="shared-links-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-brand-600 border-b border-brand-100">
                  <th className="py-2 pr-4 font-medium">Report</th>
                  <th className="py-2 pr-4 font-medium">Recipient</th>
                  <th className="py-2 pr-4 font-medium">Created</th>
                  <th className="py-2 pr-4 font-medium">Expires</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Opens</th>
                  <th className="py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-100">
                {shares.map((s) => (
                  <tr key={s.id} data-testid={`shared-link-${s.id}`}>
                    <td className="py-3 pr-4 text-brand-700 font-medium whitespace-nowrap">
                      {s.month_label}
                      <span className="text-ink-muted font-normal"> . {s.location_name}</span>
                    </td>
                    <td className="py-3 pr-4 text-ink-soft">{s.recipient_name || "."}</td>
                    <td className="py-3 pr-4 text-ink-muted whitespace-nowrap">{fmtDate(s.created_at)}</td>
                    <td className="py-3 pr-4 text-ink-muted whitespace-nowrap">{fmtDate(s.expires_at)}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 ${STATUS_STYLE[s.status] || ""}`} data-testid={`shared-link-status-${s.id}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-ink-soft">
                      <span className="inline-flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5 text-brand-400" /> {s.view_count}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      {s.status === "active" && (
                        <button
                          onClick={() => revoke(s.id)}
                          disabled={busy === s.id}
                          className="inline-flex items-center gap-1.5 text-red-600 hover:bg-red-50 px-2 py-1 text-xs font-medium disabled:opacity-60"
                          data-testid={`revoke-link-${s.id}`}
                        >
                          {busy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
